<template>
  <a-form-item-rest>
    <a-space-compact block :class="bem.b()" :size="size" v-bind="attrs">
      <ll-input
        v-model:value="inputV"
        :class="bem.e('input')"
        :placeholder="placeholder"
        :disabled="disabled"
        :allow-clear="allowClear"
        @change="handleChange"
      />

      <ll-select
        v-if="showSelect"
        v-model:value="selectV"
        :class="bem.e('select')"
        :options="processedOptions"
        :style="{ minWidth: selectMinW }"
        placeholder="请选择"
        :disabled="disabled"
        @change="handleChange"
      />
    </a-space-compact>
  </a-form-item-rest>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useAttrs, watch } from 'vue'

import { createNamespace } from '@ll-plus/utils'
import { uniqBy } from 'lodash-es'
import { emailInputEmits, emailInputProps } from './email-input'

defineOptions({ name: 'LlEmailInput', inheritAttrs: false })

const bem = createNamespace('email-input')

const props = defineProps(emailInputProps)
const emit = defineEmits(emailInputEmits)
const attrs = useAttrs()

const inputV = ref('')
const selectV = ref()

// 处理选项，确保以@开头
const processedOptions = computed(() => {
  return uniqBy(
    props.options.map((option) => {
      const processedValue = option.startsWith('@') ? option : `@${option}`
      return {
        label: processedValue,
        value: processedValue,
      }
    }),
    'value', // 根据 value 属性去重
  )
})

// 拆分邮箱值
const splitEmailValue = (value: string) => {
  if (!props.showSelect || !value) {
    return { prefix: value, suffix: null }
  }

  // 尝试匹配选项
  for (const option of processedOptions.value) {
    const suffix = option.value
    if (value.endsWith(suffix)) {
      return {
        prefix: value.slice(0, value.length - suffix.length),
        suffix,
      }
    }
  }

  // 没有匹配项
  return { prefix: value, suffix: null }
}

// 初始化拆分值
const initSplitValues = () => {
  const { prefix, suffix } = splitEmailValue(props.value)
  inputV.value = prefix
  selectV.value = suffix
}

// 更新值
const handleChange = () => {
  let newValue = inputV.value

  if (props.showSelect && selectV.value) {
    newValue += selectV.value
  }

  emit('update:value', newValue)
  emit('change', newValue)
}

// 监听props.value变化
watch(
  () => props.value,
  (newValue) => {
    const { prefix, suffix } = splitEmailValue(newValue)

    // 只有当拆分结果不同时才更新内部状态
    if (prefix !== inputV.value || suffix !== selectV.value) {
      inputV.value = prefix
      selectV.value = suffix
    }
  },
)

// 监听showSelect变化
watch(
  () => props.showSelect,
  (newShowSelect, oldShowSelect) => {
    // 从显示下拉框变为隐藏下拉框
    if (oldShowSelect && !newShowSelect) {
      // 将下拉框的值合并到输入框
      if (selectV.value) {
        inputV.value += selectV.value
        selectV.value = null
      }
      // 触发更新事件
      emit('update:value', inputV.value)
      emit('change', inputV.value)
    }
    // 从隐藏下拉框变为显示下拉框
    else if (!oldShowSelect && newShowSelect) {
      // 重新拆分当前值
      initSplitValues()
    }
  },
)

// 初始化组件
onMounted(() => {
  initSplitValues()
})
</script>
